import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const WORKFLOW_CONFIG = {
  submitted:        { label: 'Submitted',        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  assigned:         { label: 'Assigned to Rep',  color: 'bg-yellow-400/15 text-yellow-400 border-yellow-400/25' },
  discovery:        { label: 'Discovery',        color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  pricing_review:   { label: 'Pricing Review',   color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  contract_sent:    { label: 'Contract Ready',   color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  client_review:    { label: 'Under Review',     color: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
  approved:         { label: 'Approved',         color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  converted_to_job: { label: 'Project Started',  color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  declined:         { label: 'Declined',         color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const PRIORITY_COLORS = {
  urgent: 'text-red-400',
  high:   'text-orange-400',
  medium: 'text-yellow-400',
  low:    'text-secondary',
};

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const ROLE_BADGE = {
  admin:      'bg-purple-500/20 text-purple-400 border-purple-500/30',
  sales_rep:  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  client:     'bg-green-500/20 text-green-400 border-green-500/30',
};

export default function ClientServiceRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'overview');

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('service_requests')
        .select(`
          *,
          assigned_rep:profiles!service_requests_assigned_sales_rep_id_fkey(id, full_name, email)
        `)
        .eq('id', id)
        .eq('client_id', user.id)
        .single();
      if (err) throw err;
      setRequest(data);
    } catch (err) {
      setError('Request not found or access denied');
    } finally {
      setLoading(false);
    }
  }, [id, user.id]);

  const fetchMessages = useCallback(async () => {
    setMsgLoading(true);
    try {
      const { data: threadData } = await supabase
        .from('job_threads')
        .select('*')
        .eq('service_request_id', id)
        .eq('thread_type', 'external')
        .single();

      if (threadData) {
        setThread(threadData);
        const { data: msgs } = await supabase
          .from('messages')
          .select('*, sender:profiles!messages_sender_id_fkey(id, full_name, role)')
          .eq('thread_id', threadData.id)
          .order('created_at', { ascending: true });
        setMessages(msgs || []);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (_) {
    } finally {
      setMsgLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);
  useEffect(() => { if (!loading) fetchMessages(); }, [loading, fetchMessages]);

  const handleSendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      let currentThread = thread;
      if (!currentThread) {
        const { data: newThread, error: threadErr } = await supabase
          .from('job_threads')
          .insert({ service_request_id: id, thread_type: 'external' })
          .select()
          .single();
        if (threadErr) throw threadErr;
        currentThread = newThread;
        setThread(newThread);
      }

      const { data: msg, error: err } = await supabase
        .from('messages')
        .insert({ thread_id: currentThread.id, sender_id: user.id, content: newMsg.trim() })
        .select('*, sender:profiles!messages_sender_id_fkey(id, full_name, role)')
        .single();
      if (err) throw err;
      setMessages(prev => [...prev, msg]);
      setNewMsg('');
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleAcceptContract = async () => {
    setAccepting(true);
    try {
      const now = new Date().toISOString();
      const { error: err } = await supabase
        .from('service_requests')
        .update({
          contract_accepted_at: now,
          workflow_status: 'approved',
        })
        .eq('id', id)
        .eq('client_id', user.id);
      if (err) throw err;
      setRequest(prev => ({ ...prev, contract_accepted_at: now, workflow_status: 'approved' }));
    } catch (err) {
      console.error('Error accepting contract:', err);
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineContract = async () => {
    setDeclining(true);
    try {
      const { error: err } = await supabase
        .from('service_requests')
        .update({
          contract_declined_at: new Date().toISOString(),
          workflow_status: 'client_review',
          declined_reason: declineReason.trim() || null,
        })
        .eq('id', id)
        .eq('client_id', user.id);
      if (err) throw err;
      setRequest(prev => ({ ...prev, workflow_status: 'client_review', contract_declined_at: new Date().toISOString() }));
      setShowDeclineModal(false);
    } catch (err) {
      console.error('Error declining contract:', err);
    } finally {
      setDeclining(false);
    }
  };

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'chat', label: 'Chat', badge: messages.length || null },
    ...(request?.contract_sent_at ? [{ key: 'contract', label: 'Contract' }] : []),
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !request) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-red-400 mb-4 text-sm">{error}</p>
      <button onClick={() => navigate('/dashboard/client/services')} className="text-yellow-500 hover:text-yellow-400 text-sm">← Back to requests</button>
    </div>
  );

  const statusCfg = WORKFLOW_CONFIG[request.workflow_status] || WORKFLOW_CONFIG.submitted;
  const contractSent = !!request.contract_sent_at;
  const contractAccepted = !!request.contract_accepted_at;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/client/services')} className="flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          My Requests
        </button>
        <span className="text-muted">/</span>
        <span className="text-sm text-primary truncate">{request.title}</span>
      </div>

      <div className="bg-card border border-theme rounded-2xl p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-xl font-bold text-primary">{request.title}</h1>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${statusCfg.color}`}>{statusCfg.label}</span>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted">
              <span>{request.service_type}</span>
              {request.priority && <span className={`capitalize font-medium ${PRIORITY_COLORS[request.priority]}`}>{request.priority}</span>}
              <span>Submitted {new Date(request.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          {request.job_id && (
            <Link to={`/dashboard/client/jobs/${request.job_id}`} className="text-xs px-3 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/30 transition-colors flex-shrink-0">
              View Your Project →
            </Link>
          )}
        </div>

        {request.assigned_rep && (
          <div className="mt-4 pt-4 border-t border-theme flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
              <span className="text-xs font-bold text-yellow-500">{(request.assigned_rep.full_name || request.assigned_rep.email || 'R')[0].toUpperCase()}</span>
            </div>
            <div>
              <p className="text-xs text-muted">Your Account Manager</p>
              <p className="text-sm font-medium text-primary">{request.assigned_rep.full_name || request.assigned_rep.email}</p>
            </div>
          </div>
        )}
      </div>

      {contractSent && !contractAccepted && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-yellow-400 mb-1">Your contract is ready for review</p>
            <p className="text-xs text-secondary">Review the contract in the Contract tab and accept or request changes before your project begins.</p>
          </div>
          <button onClick={() => setActiveTab('contract')} className="px-4 py-2.5 bg-yellow-500 text-black font-medium rounded-xl hover:bg-yellow-400 transition-colors text-sm flex-shrink-0">
            Review Contract
          </button>
        </div>
      )}

      {contractAccepted && !request.job_id && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-sm text-green-400">Contract accepted — your project will be set up shortly.</p>
        </div>
      )}

      <div className="border-b border-theme">
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.key ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-secondary hover:text-primary'}`}>
              {tab.label}
              {tab.badge && <span className="text-xs bg-card border border-theme px-1.5 py-0.5 rounded-full">{tab.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-card border border-theme rounded-xl p-5">
              <h3 className="text-xs font-semibold text-secondary uppercase tracking-wider mb-3">Your Request</h3>
              <dl className="space-y-2.5 text-sm">
                <div className="flex justify-between"><dt className="text-muted">Service</dt><dd className="text-primary">{request.service_type}</dd></div>
                <div className="flex justify-between"><dt className="text-muted">Budget</dt><dd className="text-primary">{request.budget || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-muted">Timeline</dt><dd className="text-primary">{request.timeline_preference || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-muted">Priority</dt><dd className={`capitalize font-medium ${PRIORITY_COLORS[request.priority] || 'text-secondary'}`}>{request.priority || '—'}</dd></div>
              </dl>
            </div>

            {(request.final_agreed_price || request.proposed_price || request.scope_summary) && (
              <div className="bg-card border border-theme rounded-xl p-5">
                <h3 className="text-xs font-semibold text-secondary uppercase tracking-wider mb-3">Proposed Terms</h3>
                <dl className="space-y-2.5 text-sm">
                  {request.final_agreed_price && <div className="flex justify-between"><dt className="text-muted">Total Price</dt><dd className="text-yellow-400 font-bold">${Number(request.final_agreed_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</dd></div>}
                  {request.retainer_amount && <div className="flex justify-between"><dt className="text-muted">Deposit Due</dt><dd className="text-primary">${Number(request.retainer_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</dd></div>}
                  {request.timeline && <div className="flex justify-between"><dt className="text-muted">Timeline</dt><dd className="text-primary">{request.timeline}</dd></div>}
                  {request.revision_limit != null && <div className="flex justify-between"><dt className="text-muted">Revisions</dt><dd className="text-primary">{request.revision_limit}</dd></div>}
                </dl>
              </div>
            )}
          </div>

          {request.description && (
            <div className="bg-card border border-theme rounded-xl p-5">
              <h3 className="text-xs font-semibold text-secondary uppercase tracking-wider mb-2">Your Description</h3>
              <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{request.description}</p>
            </div>
          )}

          {request.scope_summary && (
            <div className="bg-card border border-theme rounded-xl p-5">
              <h3 className="text-xs font-semibold text-secondary uppercase tracking-wider mb-2">Agreed Scope</h3>
              <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{request.scope_summary}</p>
              {request.deliverables && (
                <>
                  <h4 className="text-xs font-semibold text-secondary uppercase tracking-wider mt-4 mb-2">Deliverables</h4>
                  <p className="text-sm text-secondary whitespace-pre-wrap">{request.deliverables}</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted">Chat directly with your account manager. All messages are saved.</p>
          <div className="bg-card border border-theme rounded-xl flex flex-col" style={{ height: '420px' }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <svg className="w-10 h-10 text-muted mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  <p className="text-sm text-muted">No messages yet. Ask your account manager a question.</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isMe = msg.sender_id === user.id;
                  return (
                    <div key={msg.id} className={`flex gap-2.5 ${isMe ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold border ${ROLE_BADGE[msg.sender?.role] || ROLE_BADGE.client}`}>
                        {(msg.sender?.full_name || '?')[0].toUpperCase()}
                      </div>
                      <div className={`max-w-[75%] flex flex-col gap-1 ${isMe ? 'items-end' : 'items-start'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted">{isMe ? 'You' : msg.sender?.full_name || 'Team'}</span>
                          <span className="text-xs text-muted">{timeAgo(msg.created_at)}</span>
                        </div>
                        <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-yellow-500/20 text-primary rounded-tr-sm' : 'bg-surface text-primary rounded-tl-sm'}`}>
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t border-theme p-3 flex gap-2">
              <textarea
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder="Send a message to your account manager…"
                rows={1}
                className="flex-1 bg-input border border-theme rounded-xl px-3 py-2 text-sm text-primary placeholder-muted focus:outline-none focus:border-yellow-500 resize-none"
              />
              <button onClick={handleSendMessage} disabled={sending || !newMsg.trim()} className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-xl hover:bg-yellow-400 disabled:opacity-50 transition-colors text-sm flex-shrink-0">
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contract' && (
        <div className="space-y-4">
          {contractAccepted ? (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-sm text-green-400 font-medium">You accepted this contract on {new Date(request.contract_accepted_at).toLocaleDateString()}</p>
            </div>
          ) : (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              <p className="text-sm text-yellow-400">Please review the contract below and accept or request changes.</p>
            </div>
          )}

          <div className="bg-card border border-theme rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-theme">
              <h3 className="text-xs font-semibold text-secondary uppercase tracking-wider">Contract</h3>
              {request.contract_version && (
                <span className="text-xs text-muted">v{request.contract_version}</span>
              )}
            </div>
            <div className="overflow-y-auto p-6 sm:p-8" style={{ maxHeight: '70vh', minHeight: '320px' }}>
              <pre className="text-sm text-primary whitespace-pre-wrap leading-[1.75] font-sans">{request.contract_content}</pre>
            </div>
          </div>

          {!contractAccepted && (
            <div className="flex flex-wrap gap-3">
              <button onClick={handleAcceptContract} disabled={accepting} className="px-6 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-500 disabled:opacity-50 transition-colors text-sm">
                {accepting ? 'Accepting…' : 'Accept Contract'}
              </button>
              <button onClick={() => setShowDeclineModal(true)} className="px-6 py-2.5 border border-theme text-secondary hover:text-red-400 hover:border-red-500/30 rounded-xl transition-colors text-sm">
                Request Changes
              </button>
            </div>
          )}
        </div>
      )}

      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-theme rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold text-primary mb-2">Request Changes</h2>
            <p className="text-sm text-secondary mb-4">Let us know what you'd like changed. Our team will review your feedback and revise the contract.</p>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Describe what you'd like changed…"
              rows={4}
              className="w-full bg-input border border-theme rounded-xl px-4 py-3 text-primary text-sm focus:outline-none focus:border-yellow-500 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button onClick={handleDeclineContract} disabled={declining} className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors text-sm">
                {declining ? 'Sending…' : 'Send Feedback'}
              </button>
              <button onClick={() => setShowDeclineModal(false)} className="flex-1 px-4 py-2.5 border border-theme text-secondary rounded-xl hover:text-primary transition-colors text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
}
